// DropdownCell.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown } from 'lucide-react';

interface DropdownOption {
  id: string;
  text: string;
  color?: string;
}

interface DropdownCellProps {
  value: any;
  config?: { options?: DropdownOption[]; multiSelect?: boolean };
  onChange?: (value: string) => void;
  readOnly?: boolean;
}

const DEFAULT_OPTIONS: DropdownOption[] = [
  { id: '1', text: 'Option 1' },
  { id: '2', text: 'Option 2' },
  { id: '3', text: 'Option 3' },
];

export const DropdownCell: React.FC<DropdownCellProps> = ({ value, config, onChange, readOnly }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const options = config?.options || DEFAULT_OPTIONS;
  const multiSelect = config?.multiSelect || false;

  let selectedOptions: DropdownOption[] = [];
  try {
    if (typeof value === 'string') {
      const parsed = JSON.parse(value);
      selectedOptions = Array.isArray(parsed) ? parsed : [parsed];
    } else if (Array.isArray(value)) {
      selectedOptions = value;
    } else if (value) {
      selectedOptions = [value];
    }
  } catch (e) {
    // Keep empty
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option: DropdownOption) => {
    if (readOnly) return;

    let newSelection: DropdownOption[];
    if (multiSelect) {
      const isSelected = selectedOptions.some((o) => o.id === option.id);
      if (isSelected) {
        newSelection = selectedOptions.filter((o) => o.id !== option.id);
      } else {
        newSelection = [...selectedOptions, option];
      }
    } else {
      newSelection = [option];
      setIsOpen(false);
    }

    if (onChange) {
      onChange(JSON.stringify(newSelection));
    }
  };

  const displayText =
    selectedOptions.length === 0
      ? '-'
      : selectedOptions.map((o) => o.text).join(', ');

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !readOnly && setIsOpen(!isOpen)}
        className="w-full px-3 py-1.5 text-sm hover:bg-gray-50 rounded flex items-center justify-between gap-2 disabled:cursor-not-allowed"
        disabled={readOnly}
      >
        <span className="truncate">{displayText}</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && !readOnly && (
        <div className="absolute z-50 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 max-h-80 overflow-y-auto">
          {options.map((option) => {
            const isSelected = selectedOptions.some((o) => o.id === option.id);
            return (
              <button
                key={option.id}
                onClick={() => handleSelect(option)}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="text-sm">{option.text}</span>
                {isSelected && <Check className="w-4 h-4 text-blue-500" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

